import _ from 'lodash';
import { useEffect } from 'react';
import { log, models, Model } from '@swiper/core';

// Global context properties:
//  error         - global error state
//  swipe         - swipe information
//  session       - active session information (*1)
//  membership    - membership information (*1)
//  member        - member information (*1)
//
// *1 - "member props" - Come from member auth or swipe.  Cleared on
//      logout or swipe expiration.

const propsets = {
  memberauth  : [ 'swipe', 'member', 'membership', 'session' ],
};

const squish = _.flow( _.flattenDeep, _.uniq, _.compact );
function without( state, ...args ) {
  const props = squish( _.map( args, arg => ( propsets[ arg ] || arg ) ) );
  return _.omit( state, props );
}

function coerce( key, action ) {
  const ModelClass = models[ _.upperFirst( key ) ];
  if ( ! ModelClass ) throw new Error( `No model for type ${key}` );
  const value = action[ key ];
  if ( ! value ) throw new Error( `Attempted to coerce ${key} without data` );
  if ( value instanceof ModelClass ) return value;
  if ( value instanceof Model ) {
    throw new Error( `Attempted to coerce a model to a different type` );
  }
  return new ModelClass( value );
}

const action_types = {
  error( state, { error } ) { return { ...state, error }; },
  swipe( state, action ) {
    const swipe = coerce( 'swipe', action );
    // - Set swipe/member/membership/session properties
    // - TODO - When swipe expires, clear these out again
    log.debug( 'SWIPE CREATED', swipe );
    const res = without( state, 'memberauth', 'overlay' );
    _.assign( res, { swipe } );
    if ( swipe.error ) {
      res.error = swipe.error;
    } else {
      _.assign( res, _.pick( swipe, 'session', 'membership', 'member' ) );
    }
    return res;
  },
  member( state, action ) {
    const member = coerce( 'member', action );
    const { membership } = member;
    return { ...without( state, 'memberauth' ), member, membership };
  },
  session( state, action ) {
    const session = coerce( 'session', action );
    return { ...state, session };
  },
  reset( state ) {
    const newState = without( state, 'memberauth' );
    log.debug( 'RESET STATE', newState );
    return newState;
  },
  no_idle( state, { no_idle } ) { return { ...state, no_idle }; },
  overlay( state, { overlay } ) { return { ...state, overlay }; },
};

export function reducer( state, { type, ...action } ) {
  const func = action_types[ type ];
  if ( ! func ) {
    log.error( `UNKNOWN ACTION type "${type}"`, action );
    return state;
  }
  try {
    return func( state, action );
  } catch( error ) {
    log.error( `GlobalContext reducer for ${type} threw error: ${error}` );
    return {};
  }
}

export function useDispatchers( state, dispatch ) {

  useEffect( () => {
    // log.debug( 'Configuring dispatchers' );
    const funcs = _.fromPairs( _.map( _.keys( action_types ), name => {
      return [ name, function dispatcher( data ) {
        if ( _.isPlainObject( data ) ) {
          const keys = _.keys( data );
          if ( keys.length === 1 && keys[0] === name ) {
            throw new Error(
              `Incorrect calling signature for dispatcher.${name}`,
            );
          }
        }
        log.debug( `Dispatching ${name}:`, data );
        dispatch( { type : name, [ name ] : data } );
        return data;
      } ];
    } ) );
    _.assign( dispatch, funcs );
    dispatch.overlay.close = () => dispatch.overlay( null );
  }, [ dispatch ] );

  return null;
}
